import { cn } from "@tellescope/ui";
import { type RouteConfig, RouterPathname, RoutesConfig, retrieveRouteComponentsFromPath } from "../../../routing";
import { useNavbarShallowStore } from "../../../store";
import { NavbarGroupHeader } from "./NavbarGroupHeader";
import { SimpleNavItem } from "./SimpleNavItem";
import { ReactNode } from "react";
import { BADGE_PATH_MAP } from "./badges";
import { useOrgFeatures } from "../../../permissions";
import { useAuth } from "@tellescope/tanstack-query";

export interface NavbarNavItemProps extends RouteConfig {
  path?: string;
}

const getVisibleSubRoutes = (subRoutes: RoutesConfig): NavbarNavItemProps[] => {
  return Object.entries(subRoutes).reduce<NavbarNavItemProps[]>((acc, [path, config]) => {
    if (config.showInNav) {
      acc.push({
        ...config,
        path,
      });
    } else {
      const visibleSubRoutes = getVisibleSubRoutes(config.subRoutes ?? {});
      if (!visibleSubRoutes.length) return acc;

      acc.push(...visibleSubRoutes);
    }

    return acc;
  }, []);
};

const parseConfig = (
  config: RouteConfig & {
    pageComponent?: ReactNode;
  }
): {
  itemType: "simple" | "group-header" | "subroutes-only" | "hidden";
  subRoutesToShow: NavbarNavItemProps[];
} => {
  const subRoutesToShow = getVisibleSubRoutes(config.subRoutes ?? {});
  if (!config.showInNav && !subRoutesToShow.length) {
    return { itemType: "hidden", subRoutesToShow };
  } else if (!config.showInNav && subRoutesToShow.length && !config.pageComponent) {
    return { itemType: "subroutes-only", subRoutesToShow };
  } else if (config.showInNav && !subRoutesToShow.length) {
    return { itemType: "simple", subRoutesToShow };
  } else if (config.showInNav && subRoutesToShow.length) {
    return { itemType: "group-header", subRoutesToShow };
  }
  return { itemType: "hidden", subRoutesToShow };
};

export const NavbarNavItem = ({ path, shouldShrinkSubroutes = true, ...config }: NavbarNavItemProps) => {
  const { isFeatureEnabled } = useOrgFeatures();
  const { selectedOrganization, session, currentUser, isAdmin } = useAuth();
  const routeComponents = path ? retrieveRouteComponentsFromPath(path) : undefined;
  const { itemType, subRoutesToShow } = parseConfig({
    ...config,
    pageComponent: routeComponents?.pageComponent,
  });
  const { toggleExpandedSection, expandedSectionPaths } = useNavbarShallowStore(
    "toggleExpandedSection",
    "expandedSectionPaths"
  );

  if (config.featureName && !isFeatureEnabled(config.featureName)) return null;
  if (
    "shouldShow" in config && typeof config.shouldShow === "function"
      ? !config.shouldShow?.({
          selectedOrganization,
          session,
          currentUser,
          isAdmin,
        })
      : typeof config.shouldShow === "boolean"
      ? !config.shouldShow
      : false
  ) {
    return null;
  }

  if (routeComponents?.customSidebarComponent) {
    return routeComponents.customSidebarComponent;
  }

  switch (itemType) {
    case "hidden":
      return null;
    case "subroutes-only":
      return (
        <div className="pl-4 flex-shrink-0">
          {subRoutesToShow.map((subRoute) => (
            <NavbarNavItem key={`${subRoute.path ?? ""}-${subRoute.label?.() ?? ""}`} {...subRoute} />
          ))}
        </div>
      );
    case "group-header": {
      const isOpen = !!path && expandedSectionPaths.includes(path);
      return (
        <>
          <NavbarGroupHeader
            isOpen={isOpen}
            onClick={() => {
              if (path) {
                toggleExpandedSection(path ?? "");
              }
            }}
            label={config.label?.() ?? ""}
            icon={config.icon}
            path={path ?? ""}
          />
          <div
            className={cn(
              "flex flex-col pl-4",
              isOpen ? "overflow-hidden" : "hidden",
              config.showSubrouteIndentBorder && "border-l border-l-border ml-3",
              !shouldShrinkSubroutes && "flex-shrink-0"
            )}
          >
            {Object.entries(config.subRoutes ?? {}).map(([subPath, config]) => (
              <NavbarNavItem key={`${path ?? ""}-${config.label?.() ?? ""}`} path={`${path}${subPath}`} {...config} />
            ))}
          </div>
        </>
      );
    }
    case "simple": {
      if (!path) return null;

      const Badge = BADGE_PATH_MAP[path as RouterPathname];

      return (
        <SimpleNavItem path={path} {...config}>
          {!!Badge && <Badge />}
        </SimpleNavItem>
      );
    }
  }
};
